import type {
  Copilot,
  CopilotAssignmentRepository,
} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {CopilotAssignmentButton} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentButton'
import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {getSessionStatusIcon} from '@github-ui/agent-sessions/components/StatusIcon'
import {
  type IssueAgentTask,
  type IssueAssignedAgentInfo,
  useIssueAgentTasks,
} from '@github-ui/agent-sessions/hooks/use-issue-agent-tasks'
import {isSessionState} from '@github-ui/agent-sessions/types/session'
import type {AgentTaskInfo} from '@github-ui/assignees'
import {Assignees} from '@github-ui/assignees'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {commitUpdateIssueAssigneesMutation} from '@github-ui/item-picker/commitUpdateIssueAssigneesMutation'
import {DEFAULT_MAXIMUM_ASSIGNEES} from '@github-ui/item-picker-assignee'
import type {AssigneeIssuePickerProps} from '@github-ui/item-picker-assignee-relay'
import {AssigneePicker, AssigneeRepositoryPicker} from '@github-ui/item-picker-assignee-relay'
import {useViewer} from '@github-ui/item-picker-assignee-relay/useViewer'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {noop} from '@github-ui/noop'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {Button} from '@primer/react'
import type {SxProp} from '@primer/styled-react'
import {type RefObject, useCallback, useMemo, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, graphql, useFragment} from 'react-relay/hooks'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import type {
  AssigneesSectionAssignees$data,
  AssigneesSectionAssignees$key,
} from './__generated__/AssigneesSectionAssignees.graphql'
import type {AssigneesSectionCopilotRepositoryFragment$key} from './__generated__/AssigneesSectionCopilotRepositoryFragment.graphql'
import type {AssigneesSectionFragment$key} from './__generated__/AssigneesSectionFragment.graphql'
import type {AssigneesSectionLazyFragment$key} from './__generated__/AssigneesSectionLazyFragment.graphql'
import type {AssigneesSectionViewer$key} from './__generated__/AssigneesSectionViewer.graphql'
import styles from './AssigneesSection.module.css'
import {addAgentAssignmentToIssueLocally, addAssigneesToIssueLocally} from './relay-tsq-interop'

const ReadonlyAssigneesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.assignees} />

// Helper function to compare two arrays of assignees by their IDs
const assigneesEqual = (a: AssigneePickerAssignee[], b: AssigneePickerAssignee[]): boolean => {
  if (a.length !== b.length) return false
  const aIds = new Set(a.map(assignee => assignee.id))
  return b.every(assignee => aIds.has(assignee.id))
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const AssigneesSectionLazyFragment = graphql`
  fragment AssigneesSectionLazyFragment on Issue {
    suggestedActors(first: 10) {
      nodes {
        ... on Actor {
          __typename
          id
          login
          name
          profileResourcePath
          avatarUrl(size: 64)
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
        }
      }
    }
  }
`

type AssigneesSectionProps = {
  sectionHeader: React.ReactElement
  onSelfAssignClick: () => void
  assignees: AssigneePickerAssignee[]
  agentAssignments?: AssigneesSectionAssignees$data['agentAssignments']
  issueAgentTasks?: IssueAgentTask[]
  agents?: IssueAssignedAgentInfo[]
  readonly: boolean
  className?: string
  copilotAssignmentButton?: React.ReactElement
} & SxProp
const AssigneesSection = ({
  sectionHeader,
  onSelfAssignClick,
  assignees,
  agentAssignments,
  issueAgentTasks,
  agents,
  readonly,
  copilotAssignmentButton,
}: AssigneesSectionProps) => {
  const emptyHeader = !readonly ? (
    <>
      {LABELS.emptySections.assignees(true)}
      <Button variant="link" onClick={onSelfAssignClick} className={styles.selfAssignButton}>
        {LABELS.emptySections.selfAssign}
      </Button>
    </>
  ) : (
    LABELS.emptySections.assignees(false)
  )

  // Wrap the copilot assignment button to match the styling of other section buttons like Unsubscribe
  const sectionFooter = copilotAssignmentButton ? (
    <div className="width-full d-flex flex-column flex-items-stretch pt-2 px-2">
      <div>{copilotAssignmentButton}</div>
    </div>
  ) : undefined

  // Map agent task that we have loaded to the agents assigned to the issue so that we can show task status in the UI.
  // We only show the most recent task for each agent.
  const agentTaskMap = useMemo(() => {
    const map = new Map<string, AgentTaskInfo>()
    if (!issueAgentTasks?.length || !agents?.length) return map

    for (const agent of agents) {
      const agentTasks = issueAgentTasks.filter(task => task.agent_collaborators?.some(c => c.agent_id === agent.id))
      if (!agentTasks.length) continue

      // only use the most recent status as the "summary" in the view
      const mostRecent = agentTasks.reduce((latest, task) =>
        new Date(task.last_updated_at).getTime() > new Date(latest.last_updated_at).getTime() ? task : latest,
      )

      if (agent.bot_global_relay_id && isSessionState(mostRecent.status)) {
        map.set(agent.bot_global_relay_id, {
          statusIcon: getSessionStatusIcon(mostRecent.status),
          count: agentTasks.length,
          taskUrl: mostRecent.url,
        })
      }
    }

    return map
  }, [issueAgentTasks, agents])

  const assigneeList = useMemo(() => {
    const baseAssignees = assignees.map(assignee => ({
      ...assignee,
      displayName: assignee.displayName || assignee.login,
      isCopilot: assignee.isCopilot ?? false,
      isAgent: assignee.isAgent ?? false,
    }))

    if (!isFeatureEnabled('issue_cca_visualization') || !agentAssignments?.length) {
      return baseAssignees
    }

    // Filter to agent assignments where both agent and assigner are still in assignedActors
    const assignedLogins = new Set(assignees.map(a => a.login))
    const validAssignments = agentAssignments.filter(
      a =>
        a?.assignee?.login &&
        a?.assigner?.login &&
        assignedLogins.has(a.assignee.login) &&
        assignedLogins.has(a.assigner.login),
    )

    if (!validAssignments.length) return baseAssignees

    const agentIds = new Set(validAssignments.map(a => a?.assignee?.id))

    return baseAssignees
      .filter(a => !agentIds.has(a.id))
      .map(assignee => ({
        ...assignee,
        assignedAgents: validAssignments
          .filter(a => a?.assigner?.login === assignee.login)
          .map(a => ({
            id: a?.assignee?.id ?? '',
            login: a?.assignee?.login ?? '',
            avatarUrl: a?.assignee?.avatarUrl ?? '',
            name: a?.assignee?.displayName ?? a?.assignee?.login ?? null,
            displayName: a?.assignee?.displayName ?? a?.assignee?.login ?? '',
            profileResourcePath: `/apps/${a?.assignee?.login}`,
            isCopilot: a?.assignee?.isCopilot ?? false,
            isAgent: a?.assignee?.isAgent ?? true,
            agentTaskInfo: agentTaskMap.get(a?.assignee?.id ?? '') ?? undefined,
          })),
      }))
  }, [assignees, agentAssignments, agentTaskMap])

  return (
    <Section
      id={`sidebar-assignees-section`}
      sectionHeader={sectionHeader}
      sectionFooter={sectionFooter}
      emptyText={assignees.length > 0 ? undefined : emptyHeader}
    >
      <Assignees assignees={assigneeList} testId={TEST_IDS.assignees} />
    </Section>
  )
}

export type CreateIssueAssigneesSectionProps = {
  repo: string
  owner: string
  readonly: boolean
  assignees: AssigneePickerAssignee[]
  onSelectionChange: (value: AssigneePickerAssignee[]) => void
  insidePortal: boolean
  className?: string
  repository?: CopilotAssignmentRepository // Repository object for Copilot assignment
  issueNumber?: number
  onIssueUpdate?: () => void
} & SxProp
export function CreateIssueAssigneesSection({
  repo,
  owner,
  readonly,
  assignees,
  onSelectionChange,
  className,
  sx,
  repository,
  issueNumber,
  onIssueUpdate,
  ...sharedConfigProps
}: CreateIssueAssigneesSectionProps) {
  const viewer = useViewer()

  const pickerProps = {
    repo,
    owner,
    readonly,
    includeAuthorableBots: false,
    includeAssignableBots: true,
    assigneeTokens: [],
    assignees,
    onSelectionChange,
    anchorElement: (anchorProps: React.HTMLAttributes<HTMLElement>, ref?: RefObject<HTMLButtonElement | null>) => (
      <SectionHeader
        title={LABELS.sectionTitles.assignees}
        buttonProps={{...anchorProps, id: 'assignees-select-menu'}}
        ref={ref}
      />
    ),
    ...sharedConfigProps,
  }

  const sectionHeader = readonly ? <ReadonlyAssigneesSectionHeader /> : <AssigneeRepositoryPicker {...pickerProps} />

  return (
    <AssigneesSection
      sectionHeader={sectionHeader}
      onSelfAssignClick={() => onSelectionChange(viewer ? [viewer] : [])}
      assignees={assignees}
      readonly={readonly}
      sx={sx}
      className={className}
    />
  )
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const assigneesSectionAssignees = graphql`
  fragment AssigneesSectionAssignees on Issue {
    assignedActors(first: 20) {
      nodes {
        ... on Actor {
          __typename
          id
          login
          name
          profileResourcePath
          avatarUrl(size: 64)
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
        }
      }
    }
    agentAssignments {
      assignee {
        id
        login
        avatarUrl(size: 64)
        isCopilot
        isAgent
        displayName
      }
      assigner {
        __typename
        ... on Node {
          id
        }
        ... on Actor {
          login
        }
      }
    }
  }
`

// Fragment for repository data needed by CopilotAssignmentModal
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const assigneesSectionCopilotRepositoryFragment = graphql`
  fragment AssigneesSectionCopilotRepositoryFragment on Repository {
    # Used to determine whether to render Copilot modal
    isArchived
    # Checks if Assign to Copilot should be shown
    isCopilotAgentEnabled
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const assigneesSectionFragment = graphql`
  fragment AssigneesSectionFragment on Issue {
    id
    number
    repository {
      name
      owner {
        login
      }
      isArchived
      ...AssigneesSectionCopilotRepositoryFragment
    }
    ...AssigneesSectionAssignees
    viewerCanAssign
  }
`

export type EditIssueAssigneesSectionProps = {
  issue: AssigneesSectionFragment$key
  viewer: AssigneesSectionViewer$key | null
  lazyKey?: AssigneesSectionLazyFragment$key
  onIssueUpdate?: () => void
  insideSidePanel?: boolean
  readonly: boolean
}
export function EditIssueAssigneesSection({
  issue,
  viewer,
  lazyKey,
  onIssueUpdate,
  insideSidePanel,
  readonly,
}: EditIssueAssigneesSectionProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const data = useFragment(assigneesSectionFragment, issue)
  const {
    repository,
    repository: {
      owner: {login: owner},
      name: repo,
      isArchived: isRepositoryArchived,
    },
    number,
  } = data

  const copilotRepositoryData = useFragment<AssigneesSectionCopilotRepositoryFragment$key>(
    assigneesSectionCopilotRepositoryFragment,
    data.repository,
  )

  const assigneesData = useFragment<AssigneesSectionAssignees$key>(assigneesSectionAssignees, data)

  const viewerData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment AssigneesSectionViewer on Actor {
        __typename
        id
        login
        name
        profileResourcePath
        avatarUrl(size: 64)
        ... on Bot {
          isCopilot
          isAgent
          displayName
        }
      }
    `,
    viewer,
  )

  const {id: issueId, viewerCanAssign} = data
  const lazyData = useFragment(AssigneesSectionLazyFragment, lazyKey)

  const suggestions = useMemo(() => {
    return (lazyData?.suggestedActors?.nodes || []).flatMap(actor => (actor ? [actor] : [])) as AssigneePickerAssignee[]
  }, [lazyData?.suggestedActors?.nodes])

  const assignees = useMemo(() => {
    return (assigneesData.assignedActors?.nodes || []).flatMap(actor =>
      actor ? [actor] : [],
    ) as AssigneePickerAssignee[]
  }, [assigneesData.assignedActors?.nodes])

  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()
  const copilotButtonRef = useRef<HTMLButtonElement>(null)

  const {data: issueAgentTasksData} = useIssueAgentTasks({owner, repo, issueNumber: number})

  const assignActorWithAgentEnabled = isFeatureEnabled('issues_cca_assign_actor_with_agent')

  const readonlyAssignee = readonly || !viewerCanAssign || isRepositoryArchived

  const canUseCopilotAssignment = useMemo(() => {
    const isCopilotCurrentlyAssigned = assignees.some(assignee => isCopilot(assignee.login))
    return !readonlyAssignee && copilotRepositoryData.isCopilotAgentEnabled && !isCopilotCurrentlyAssigned
  }, [assignees, readonlyAssignee, copilotRepositoryData.isCopilotAgentEnabled])

  // State for managing Copilot modal triggered from both button and assignee picker
  const [copilotModalOpen, setCopilotModalOpen] = useState(false)
  const [pickerInstanceKey, setPickerInstanceKey] = useState(0)

  const handleOpenCopilotModal = useCallback(() => {
    setCopilotModalOpen(true)
    /* Force a remount of the picker so it resets to initialSelectedItems (equivalent to Cancel)
      This is needed because when we do nothing with Copilot, it remains selected in the picker
      and the modal would reopen immediately when the picker opens */
    setPickerInstanceKey(prev => prev + 1)
  }, [setCopilotModalOpen])

  const handleCloseCopilotModal = useCallback(() => {
    setCopilotModalOpen(false)
  }, [setCopilotModalOpen])

  const updateAssignees = useCallback(
    (
      selectedAssignees: AssigneePickerAssignee[],
      newAgentAssignments?: AssigneesSectionAssignees$data['agentAssignments'],
    ) => {
      const existingAgentAssignments = isFeatureEnabled('issue_cca_visualization')
        ? assigneesData.agentAssignments
        : undefined
      commitUpdateIssueAssigneesMutation({
        environment,
        input: {issueId, assignedActors: selectedAssignees, participants: suggestions},
        existingAgentAssignments: newAgentAssignments?.length
          ? [...(existingAgentAssignments ?? []), ...newAgentAssignments]
          : existingAgentAssignments,
        onError: () =>
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateAssignees,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, issueId, onIssueUpdate, suggestions, assigneesData.agentAssignments],
  )

  const updateAssigneesWithCopilotDetection = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      // Calculate if Copilot was previously assigned
      const wasCopilotPreviouslyAssigned = assignees.some(assignee => isCopilot(assignee.login))

      // Check if Copilot is in the selection and feature is enabled
      const hasCopilotAssignee = selectedAssignees.some(assignee => assignee.isCopilot)
      const nonCopilotAssignees = selectedAssignees.filter(assignee => !assignee.isCopilot)

      let assigneesToSave = canUseCopilotAssignment ? nonCopilotAssignees : selectedAssignees
      let newAgentAssignments: AssigneesSectionAssignees$data['agentAssignments'] | undefined

      // Add viewer to assignees when a 3rd party agent is being added
      if (assignActorWithAgentEnabled && viewerData) {
        // Check if a 3rd party agent (non-Copilot) is being added
        const previousAgentLogins = new Set(assignees.filter(a => a.isAgent && !isCopilot(a.login)).map(a => a.login))
        const thirdPartyAgentsAdded = selectedAssignees.filter(
          assignee => assignee.isAgent && !isCopilot(assignee.login) && !previousAgentLogins.has(assignee.login),
        )

        if (thirdPartyAgentsAdded.length > 0) {
          const isViewerAlreadyAssigned = assignees.some(assignee => assignee.login === viewerData.login)
          const isViewerInSelection = selectedAssignees.some(assignee => assignee.login === viewerData.login)

          const shouldAddViewer =
            !isViewerAlreadyAssigned && !isViewerInSelection && selectedAssignees.length < DEFAULT_MAXIMUM_ASSIGNEES

          if (shouldAddViewer) {
            assigneesToSave = [...assigneesToSave, viewerData as AssigneePickerAssignee]
          }

          newAgentAssignments = thirdPartyAgentsAdded.map(agent => ({
            assignee: {
              id: agent.id,
              login: agent.login,
              avatarUrl: agent.avatarUrl,
              isCopilot: agent.isCopilot ?? false,
              isAgent: agent.isAgent ?? true,
              displayName: agent.displayName ?? agent.login,
            },
            assigner: {
              __typename: viewerData.__typename,
              id: viewerData.id,
              login: viewerData.login,
            },
          }))
        }
      }

      // Only check for unnecessary mutations when Copilot assignment is enabled
      // When disabled, always save to maintain existing behavior
      if (canUseCopilotAssignment) {
        // Only save if the resulting assignees list is different from current assignees
        // This prevents unnecessary mutations when only Copilot is removed/added
        const currentNonCopilotAssignees = assignees.filter(assignee => !isCopilot(assignee.login))

        if (!assigneesEqual(assigneesToSave, currentNonCopilotAssignees)) {
          updateAssignees(assigneesToSave, newAgentAssignments)
        }
      } else {
        updateAssignees(assigneesToSave, newAgentAssignments)
      }

      // Only open modal if Copilot is being ADDED (not removed)
      // This checks: Copilot is in new selection AND was not previously assigned
      if (hasCopilotAssignee && canUseCopilotAssignment && !wasCopilotPreviouslyAssigned) {
        handleOpenCopilotModal()
      }
    },
    [
      assignees,
      canUseCopilotAssignment,
      updateAssignees,
      handleOpenCopilotModal,
      assignActorWithAgentEnabled,
      viewerData,
    ],
  )

  const onAssignmentComplete = useCallback(
    (copilot?: Copilot) => {
      // After successful Copilot assignment, directly update the Relay store to include Copilot and the viewer as assignees
      if (copilot) {
        commitLocalUpdate(environment, store => {
          const assigneesToAdd: AssigneePickerAssignee[] = [
            {
              ...copilot,
              isCopilot: true,
              __typename: 'Bot',
            },
          ]
          const isViewerAlreadyAssigned = assignees.some(assignee => assignee.login === viewerData?.login)
          const shouldAddViewer =
            assignActorWithAgentEnabled && !isViewerAlreadyAssigned && assignees.length + 1 < DEFAULT_MAXIMUM_ASSIGNEES
          if (viewerData && shouldAddViewer) {
            assigneesToAdd.push(viewerData as AssigneePickerAssignee)
          }
          addAssigneesToIssueLocally(store, issueId, assigneesToAdd)
          if (viewerData) {
            addAgentAssignmentToIssueLocally(store, issueId, copilot, viewerData)
          }
        })
      }

      onIssueUpdate?.()
    },
    [assignActorWithAgentEnabled, assignees, environment, issueId, onIssueUpdate, viewerData],
  )

  const copilotAssignmentButton = canUseCopilotAssignment ? (
    <CopilotAssignmentButton onClick={handleOpenCopilotModal} ref={copilotButtonRef} />
  ) : undefined

  // Modal for Copilot assignment triggered from both button and assignee picker
  const copilotAssignmentModal = canUseCopilotAssignment ? (
    <CopilotAssignmentModal
      isOpen={copilotModalOpen}
      onClose={handleCloseCopilotModal}
      repository={{
        name: repository.name,
        owner: {
          login: repository.owner.login,
        },
      }}
      issueNumbers={[number]}
      onAssignmentComplete={onAssignmentComplete}
      returnFocusRef={copilotButtonRef}
    />
  ) : undefined

  const assigneeSectionHeader = useMemo(() => {
    if (readonlyAssignee) {
      return <ReadonlyAssigneesSectionHeader />
    }

    const pickerProps: AssigneeIssuePickerProps = {
      repo,
      owner,
      number,
      anchorElement: (props: React.HTMLAttributes<HTMLElement>, ref?: RefObject<HTMLButtonElement | null>) => (
        <SectionHeader
          title={LABELS.sectionTitles.assignees}
          buttonProps={{
            ...props,
            onClick: e => {
              sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_ASSIGNEE_SECTION_EDIT_CLICK')
              props.onClick?.(e)
            },
          }}
          ref={ref}
        />
      ),
      readonly: readonlyAssignee,
      assignees,
      onSelectionChange: (selectedAssignees: AssigneePickerAssignee[]) =>
        updateAssigneesWithCopilotDetection(selectedAssignees),
      insidePortal: insideSidePanel,
      suggestions,
    }

    return <AssigneePicker key={`assignee-picker-${pickerInstanceKey}`} {...pickerProps} />
  }, [
    readonlyAssignee,
    repo,
    owner,
    number,
    assignees,
    insideSidePanel,
    suggestions,
    pickerInstanceKey,
    sendAnalyticsEvent,
    updateAssigneesWithCopilotDetection,
  ])

  return (
    <>
      <AssigneesSection
        sectionHeader={assigneeSectionHeader}
        assignees={assignees}
        agentAssignments={assigneesData.agentAssignments}
        issueAgentTasks={issueAgentTasksData?.tasks}
        agents={issueAgentTasksData?.assigned_agents}
        onSelfAssignClick={() => (viewerData ? updateAssignees([viewerData as AssigneePickerAssignee]) : noop)}
        readonly={readonlyAssignee}
        copilotAssignmentButton={copilotAssignmentButton}
      />
      {copilotAssignmentModal}
    </>
  )
}
